import {Fragment, useEffect, useState} from 'react'

import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import {KEY_PAIR_POLL_ATTEMPTS, KEY_PAIR_POLL_INTERVAL} from '../../../lib/recovery'

/**
 * The only screen that moves on its own, because it is the only one waiting on a background job
 * rather than on a person.
 *
 * It gives up out loud. A generation that fails cleanly records itself and this screen is replaced,
 * but one whose job never ran at all leaves the record sitting exactly here, and nothing on this
 * side can tell that from a job a second away.
 */
const PreparingRecovery = props => {
  const [attempts, setAttempts] = useState(0)
  const stalled = attempts >= KEY_PAIR_POLL_ATTEMPTS

  useEffect(() => {
    if (stalled)
      return undefined

    const interval = setInterval(() => {
      setAttempts(count => count + 1)
      props.onRefresh()
    }, KEY_PAIR_POLL_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [stalled])

  const checkAgain = () => {
    setAttempts(0)
    props.onRefresh()
  }

  return (
    <Paper elevation={3} component='div' className='recovery-step'>
      <Typography variant='h4' gutterBottom>Getting your account ready</Typography>
      <Typography variant='body1' gutterBottom>
        We're preparing the handover so your contacts can send their pieces back. This usually takes
        a few seconds.
      </Typography>
      {
        stalled ? (
          <Fragment>
            <Typography className='caption' variant='body2' color='error' gutterBottom>
              This is taking longer than it should. Nothing has been lost — your contacts still hold
              their pieces.
            </Typography>
            <Button onClick={checkAgain} variant='contained' sx={{color: '#F7F7F7'}}>Check again</Button>
          </Fragment>
        ) : (
          <Fragment>
            <LinearProgress className='progress' />
            <Typography className='caption' variant='body2'>
              You don't need to do anything — this page updates on its own.
            </Typography>
          </Fragment>
        )
      }
    </Paper>
  )
}

PreparingRecovery.propTypes = {onRefresh: PropTypes.func.isRequired}

export default PreparingRecovery
